/**
 * Must stay in sync with @achieve/cx-event-horizon consent constants.
 * `__tests__/eventHorizonTealiumBootstrap.test.js` pins these to the package.
 */
export const CONSENT_CHANGED_EVENT = 'event-horizon:consent-changed'
export const DEFAULT_CONSENT_COOKIE_NAME = 'eh_consent_v1'
export const TEALIUM_SCRIPT_ID = 'event-horizon-tealium-utag'
export const CONSENT_VERSION = '1.0.0'

export const VALID_CONSENT_SOURCES = [
  'banner_ok',
  'banner_close',
  'navigation_click',
  'button_click',
  'input_click',
]

/**
 * Inline bootstrap: load Tealium only when eh_consent_v1 is valid (or a legacy banner dismiss
 * cookie is present, ADR-011); listen for consent-changed otherwise.
 * @param {{ src: string, cookieName?: string, legacyCookieNames?: string[] }} config
 */
export function buildEventHorizonTealiumBootstrap(config) {
  const serialized = JSON.stringify({
    src: config.src,
    cookieName: config.cookieName ?? DEFAULT_CONSENT_COOKIE_NAME,
    legacyCookieNames: config.legacyCookieNames ?? [],
  }).replace(/</g, '\\u003c')

  return `
(function () {
  var config = ${serialized};
  var scriptId = "${TEALIUM_SCRIPT_ID}";
  var validSources = ${JSON.stringify(VALID_CONSENT_SOURCES)};
  var consentEvent = "${CONSENT_CHANGED_EVENT}";
  var consentVersion = "${CONSENT_VERSION}";

  function getCookie(name) {
    var prefix = encodeURIComponent(name) + "=";
    var cookies = document.cookie.split(";");

    for (var i = 0; i < cookies.length; i++) {
      var cookie = cookies[i].trim();

      if (cookie.indexOf(prefix) === 0) {
        return decodeURIComponent(cookie.slice(prefix.length));
      }
    }

    return null;
  }

  // Presence check only: never decode the value, a malformed one must not abort the bootstrap.
  function hasLegacyBannerCookie() {
    var cookies = document.cookie.split(";");

    for (var i = 0; i < cookies.length; i++) {
      var name = cookies[i].split("=")[0].trim();

      if (config.legacyCookieNames.indexOf(name) !== -1) {
        return true;
      }
    }

    return false;
  }

  function hasConsent() {
    try {
      var value = getCookie(config.cookieName);
      var record = value ? JSON.parse(value) : null;

      if (
        record &&
        record.version === consentVersion &&
        record.marketingEligible === true &&
        validSources.indexOf(record.source) !== -1 &&
        (!record.expiresAt || record.expiresAt > Date.now())
      ) {
        return true;
      }
    } catch (_) {}

    return hasLegacyBannerCookie();
  }

  function loadTealium() {
    if (!hasConsent() || document.getElementById(scriptId)) {
      return;
    }

    var script = document.createElement("script");
    script.id = scriptId;
    script.async = true;
    script.src = config.src;
    document.head.appendChild(script);
  }

  window.addEventListener(consentEvent, loadTealium);
  loadTealium();
})();
`
}
